import { useEffect, useState } from 'react';
import { Flexbox, Text } from '@morressier/ts';
import {
  faSquarePen,
  faMagnifyingGlass,
  faSquarePlus
} from '@fortawesome/pro-regular-svg-icons';

import { faBook } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MissingProfileIndicator from '../MissingProfileIndicator';
import { useRouter } from 'next/router';
import { useAllResearch } from 'clients/internal/homeSearch';
import { LinkButton } from 'components/ProfileUIComponents';
import { SubmissionCard } from 'components/Cards';
import Researchmodal from './Researchmodal';
import { KeyedMutator } from 'swr';
import { IResearch } from 'typings/profile';
import { ISubmissionSearchResult } from 'typings/submission';
import { logEvent } from 'utils/eventTracking';

interface IResearchProps {
  isSameUser?: boolean;
  userId: string;
  research: ISubmissionSearchResult[];
  mutate: KeyedMutator<IResearch>;
  isLoading: boolean;
}

const Research = (props: IResearchProps) => {
  const { isSameUser, userId, research = [], mutate, isLoading } = props;
  const [showResearchEditForm, setShowResearchEditForm] = useState(false);
  const openResearchEditForm = () => setShowResearchEditForm(true);
  const closeResearchEditForm = () => setShowResearchEditForm(false);

  const router = useRouter();
  const { data: allResearch, isLoading: isAllResearchLoading } =
    useAllResearch(userId);

  const url = encodeURIComponent(`${origin}${router.asPath}`);

  const handleAddMissingResearchAction = () => {
    logEvent('PROFILE_PAGE_ADD_RESEARCH_CLICKED');
    openResearchEditForm();
  };

  const handleEditResearchAction = () => {
    logEvent('PROFILE_RESEARCH_EDIT_CLICKED');
    openResearchEditForm();
  };

  const handleSubmissionLogEvent = () => {
    if (isSameUser)
      logEvent('PROFILE_RESEARCH_CLICKED_OTHER_USER_RESEARCH_CARD');
    else logEvent('PROFILE_RESEARCH_CLICKED_OWN_RESEARCH_CARD');
  };

  return (
    <div id="research">
      <Flexbox
        gap="8px"
        flexDirection="row"
        alignItems="center"
        style={{ marginBottom: '1rem' }}
      >
        <Text size="h4_new" fontWeight="semiBold">
          Research
        </Text>
        {research?.length > 0 && isSameUser && (
          <LinkButton onClick={handleEditResearchAction}>
            <FontAwesomeIcon icon={faSquarePen} />
            Edit
          </LinkButton>
        )}
      </Flexbox>
      {allResearch.length === 0 &&
        isSameUser &&
        !isLoading &&
        !isAllResearchLoading && (
          <MissingProfileIndicator
            handleAction={() => {
              logEvent('PROFILE_RESEARCH_SEE_CALLS_FOR_ABSTRACTS_PAPERS');
              router.push('/search/call-for-abstracts');
            }}
            icon={faBook}
            iconColor="var(--color-mono-90)"
            backgroundColor="var(--color-mono-100)"
            actionIcon={faMagnifyingGlass}
            title="No research found on the platform"
            actiontitle="Find Calls for Abstracts and Calls for Papers in Morressier"
            description="You must have research on the Morressier platform in order to add it to your profile."
          />
        )}
      {allResearch.length > 0 &&
        research?.length === 0 &&
        isSameUser &&
        !isLoading && (
          <MissingProfileIndicator
            handleAction={handleAddMissingResearchAction}
            icon={faBook}
            actionIcon={faSquarePlus}
            title="Add your research"
            actiontitle="Add research"
            description="Provide insight into your work by adding representative examples of your research."
          />
        )}
      <Flexbox gap="1rem" flexDirection="column">
        {research.map(submission => (
          <SubmissionCard
            key={submission.id}
            submission={submission}
            submissionId={submission.id}
            currentPath={url}
            logEvent={handleSubmissionLogEvent}
          />
        ))}
      </Flexbox>
      {!isLoading && (
        <Researchmodal
          isOpen={showResearchEditForm}
          onDismiss={closeResearchEditForm}
          selectedResearch={research.map(({ id }) => id)}
          researchList={allResearch}
          userId={userId}
          mutateResearchList={mutate}
        />
      )}
    </div>
  );
};

export default Research;
